import * as React from 'react';

/**
 * Colour theme: light, dark, or follow the operating system.
 *
 * The choice is a per-browser preference, so it lives in `localStorage` rather than in
 * the business settings document on the server. The effective theme is applied as a
 * `dark` class on `<html>`; every colour in the app is a CSS token, and `index.css`
 * redefines the tokens under `.dark`.
 *
 * `public/theme-init.js` applies the stored choice synchronously before first paint
 * (an inline script would be blocked by the `script-src 'self'` CSP). Keep its storage
 * key and logic in step with this file.
 */
export type ThemePreference = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = 'albuelis-accounting.theme';

const PREFERENCES: ReadonlyArray<ThemePreference> = ['light', 'dark', 'system'];
const DARK_QUERY = '(prefers-color-scheme: dark)';
const CHANGE_EVENT = 'albuelis-accounting:theme-change';

export function getThemePreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && (PREFERENCES as string[]).includes(stored)) return stored as ThemePreference;
  } catch {
    // Storage can be unavailable (privacy mode); fall through to the default.
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

/** The theme actually shown for a preference: `system` resolves through the OS setting. */
export function resolveTheme(preference: ThemePreference): 'light' | 'dark' {
  if (preference === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return preference;
}

export function applyTheme(preference: ThemePreference): void {
  const dark = resolveTheme(preference) === 'dark';
  const root = document.documentElement;
  root.classList.toggle('dark', dark);
  root.style.colorScheme = dark ? 'dark' : 'light';
}

export function setThemePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Not persisted, but still applied for this page view.
  }
  applyTheme(preference);
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/**
 * Apply the stored preference and keep `system` in step with the OS while the app is
 * open. Called once at startup; returns a teardown for tests.
 */
export function initTheme(): () => void {
  applyTheme(getThemePreference());
  if (typeof window.matchMedia !== 'function') return () => {};
  const media = window.matchMedia(DARK_QUERY);
  const onSystemChange = () => {
    if (getThemePreference() === 'system') applyTheme('system');
  };
  media.addEventListener('change', onSystemChange);
  return () => media.removeEventListener('change', onSystemChange);
}

/** The current preference and a setter; re-renders when it changes anywhere in the app. */
export function useThemePreference(): [ThemePreference, (preference: ThemePreference) => void] {
  const preference = React.useSyncExternalStore(
    (onChange) => {
      window.addEventListener(CHANGE_EVENT, onChange);
      window.addEventListener('storage', onChange);
      return () => {
        window.removeEventListener(CHANGE_EVENT, onChange);
        window.removeEventListener('storage', onChange);
      };
    },
    getThemePreference,
    () => 'system' as ThemePreference,
  );
  return [preference, setThemePreference];
}
